// Minimal, safe Markdown renderer for assistant replies.
//
// The app has no Markdown library, and the assistant's output is limited to
// paragraphs, bold/italic/code, bullet and numbered lists, headings and simple
// pipe tables. Everything is rendered as React text nodes; no HTML is injected.

import React from 'react';
import PropTypes from 'prop-types';

const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*|_[^_\n]+_)/g;

const withIds = (items, prefix) => items
  .map((value, position) => ({ id: `${prefix}-${position}`, value }));

function renderInline(text, keyPrefix) {
  const parts = withIds(text.split(INLINE_RE).filter((p) => p !== ''), keyPrefix);
  return parts.map(({ id: key, value: part }) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={key}>{part.slice(1, -1)}</code>;
    }
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
      if (part.length > 2) return <em key={key}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

const isTableRow = (line) => /^\s*\|.*\|\s*$/.test(line);
const isTableSeparator = (line) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
const splitCells = (line) => line.trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((c) => c.trim());

function parseBlocks(markdown) {
  const lines = (markdown || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  const push = (block) => blocks.push({ ...block, id: `b-${blocks.length}` });
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; } // eslint-disable-line no-continue

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i += 1;
      continue; // eslint-disable-line no-continue
    }

    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitCells(line);
      const rows = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitCells(lines[i]));
        i += 1;
      }
      push({ type: 'table', header, rows });
      continue; // eslint-disable-line no-continue
    }

    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const items = [];
      while (i < lines.length) {
        const m = ordered ? lines[i].match(/^\s*\d+[.)]\s+(.*)$/) : lines[i].match(/^\s*[-*•]\s+(.*)$/);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      push({ type: ordered ? 'ol' : 'ul', items });
      continue; // eslint-disable-line no-continue
    }

    const para = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !/^(#{1,4})\s/.test(lines[i])
      && !/^\s*[-*•]\s+/.test(lines[i]) && !/^\s*\d+[.)]\s+/.test(lines[i]) && !isTableRow(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    push({ type: 'p', text: para.join(' ') });
  }
  return blocks;
}

const renderList = (b) => {
  const items = withIds(b.items, b.id);
  const children = items.map(({ id, value }) => <li key={id}>{renderInline(value, id)}</li>);
  return b.type === 'ol' ? <ol key={b.id}>{children}</ol> : <ul key={b.id}>{children}</ul>;
};

const renderTable = (b) => {
  const header = withIds(b.header, `${b.id}-h`);
  const rows = withIds(b.rows, `${b.id}-r`);
  return (
    <div key={b.id} className="rhinoanalyst__md__table-wrap">
      <table className="rhinoanalyst__md__table">
        <thead>
          <tr>{header.map(({ id, value }) => <th key={id}>{renderInline(value, id)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {withIds(row.value, row.id).map(({ id, value }) => <td key={id}>{renderInline(value, id)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RhinoAnalystMarkdown = ({ text }) => {
  const blocks = parseBlocks(text);
  return (
    <div className="rhinoanalyst__md">
      {blocks.map((b) => {
        switch (b.type) {
          case 'heading':
            return (
              <div key={b.id} className={`rhinoanalyst__md__heading rhinoanalyst__md__heading--${b.level}`}>
                {renderInline(b.text, b.id)}
              </div>
            );
          case 'ul':
          case 'ol':
            return renderList(b);
          case 'table':
            return renderTable(b);
          default:
            return <p key={b.id}>{renderInline(b.text, b.id)}</p>;
        }
      })}
    </div>
  );
};

RhinoAnalystMarkdown.propTypes = {
  text: PropTypes.string,
};

RhinoAnalystMarkdown.defaultProps = {
  text: '',
};

export default RhinoAnalystMarkdown;
